import {
  OktonInput,
  OktonAlert,
  OktonSelect,
  OktonPageHeader,
} from '@okton/platform-ui';
import {
  Box,
  Button,
  Flex,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { Check } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import BankAppPage from '../../../layout/BankAppPage';
import {
  usePlanoDFC,
  useSavePlanoDFC,
  useSugestaoPlanoDFC,
} from '../../../services/hooks/usePlanoDFC';

const VAZIO = {
  tipo: 'nivel' as 'nivel' | 'conta',
  item: '',
  nome: '',
  conta: '',
};

/**
 * Cadastro de um Nível ou de uma Conta da DFC.
 *
 * O Nível só agrupa e por isso não tem Conta; a Conta é a folha que recebe
 * lançamento. É o item que define onde o registro entra na árvore: o Nível
 * acima é o item sem o último trecho, e ele precisa existir.
 *
 * As validações valem também no servidor — a tela adianta o que dá para
 * conferir sem ir ao banco, e quem barra de verdade é ele.
 */
const PlanoContasForm = () => {
  const history = useHistory();
  const bg = useColorModeValue('white', 'gray.800');
  const color = useColorModeValue('#3f3f46', '#d4d4d8');
  const { idPlano } = useParams<{ idPlano?: string }>();
  const edicao = !!idPlano;

  const { data: linhas = [] } = usePlanoDFC();
  const saveMutation = useSavePlanoDFC();

  const [form, setForm] = useState({ ...VAZIO });
  const [erro, setErro] = useState('');

  const registro = linhas.find(l => l.id_pct === idPlano);

  // A lista chega depois do primeiro render: sem isso a edição abriria vazia.
  useEffect(() => {
    if (!registro) return;
    setForm({
      tipo: registro.conta_pct ? 'conta' : 'nivel',
      item: registro.item_pct,
      nome: registro.nome_pct,
      conta: registro.conta_pct ?? '',
    });
  }, [registro]);

  // Mesmo cálculo do servidor, só para mostrar em qual Nível o registro vai
  // entrar antes de salvar.
  const paiDoItem = (item: string) => {
    const limpo = item.trim().replace(/\.+$/, '');
    const ponto = limpo.lastIndexOf('.');
    if (ponto === -1) return `${limpo}.`;
    const pai = limpo.slice(0, ponto);
    return pai.includes('.') ? pai : `${pai}.`;
  };

  const itemValido = /^[0-9]+(\.[0-9]+)*\.?$/.test(form.item.trim());
  const pai = itemValido ? paiDoItem(form.item) : '';
  const ehRaiz = pai === `${form.item.trim().replace(/\.+$/, '')}.`;
  const nivelPai = linhas.find(l => l.item_pct === pai);

  // O próximo número livre sai do servidor, que olha o Plano de Contas
  // inteiro — a Conta não pode repetir nem na DRE, que a tela não carrega.
  // Só faz sentido com o Nível em mãos, e em edição o número já existe.
  const { data: sugestao } = useSugestaoPlanoDFC(
    !edicao && nivelPai ? pai : '',
  );
  const itemRepetido =
    !edicao && itemValido && linhas.some(l => l.item_pct === form.item.trim());

  // Sugestão é ponto de partida, não imposição: preenche o campo vazio e
  // não volta a mexer depois que o usuário digitar o número dele.
  useEffect(() => {
    if (!sugestao?.conta) return;
    setForm(f =>
      f.tipo === 'conta' && f.conta.trim() === ''
        ? { ...f, conta: String(sugestao.conta) }
        : f,
    );
  }, [sugestao]);

  const salvar = async () => {
    const item = form.item.trim();
    if (item === '') {
      setErro('Informe o Item.');
      return;
    }
    if (!itemValido) {
      setErro('O Item só aceita números separados por ponto, como 1.1.2.');
      return;
    }
    // Mesma regra do IFLEX: 3 dígitos no formato do DRE ('001'), 1 ou 2 no da
    // DFC ('1', sem zero à esquerda).
    const segmentos = item.replace(/\.+$/, '').split('.');
    const ultimo = segmentos[segmentos.length - 1];
    if (ultimo.length < 1 || ultimo.length > 3) {
      setErro('Item no formato inválido.');
      return;
    }
    if (!form.nome.trim()) {
      setErro('Informe o Nome.');
      return;
    }
    if (form.tipo === 'conta' && !form.conta.trim()) {
      setErro('Informe a Conta.');
      return;
    }
    if (!ehRaiz && !nivelPai) {
      setErro(`O Nível ${pai} não existe. Cadastre-o antes.`);
      return;
    }
    setErro('');
    try {
      await saveMutation.mutateAsync({
        id: idPlano,
        tipo: form.tipo,
        item,
        nome: form.nome.trim(),
        conta: form.tipo === 'conta' ? form.conta.trim() : '',
      });
      history.push('/plano-contas');
    } catch (err) {
      setErro(
        err instanceof Error
          ? err.message
          : 'Erro ao gravar o Plano de Contas.',
      );
    }
  };

  return (
    <BankAppPage>
      <OktonPageHeader
        eyebrow="CADASTROS"
        title={edicao ? 'Editar Plano de Contas' : 'Novo Plano de Contas'}
        subtitle="Nível ou Conta da Demonstração dos Fluxos de Caixa"
        onBack={() => history.push('/plano-contas')}
      />

      <Box bg={bg} mt="4" borderWidth="1px" rounded="2xl" p="6">
        <Stack spacing="4" maxW="720px">
          {!!erro && <OktonAlert status="error" description={erro} />}

          <Box>
            <Text
              as="label"
              htmlFor="tipo"
              display="block"
              fontSize="sm"
              fontWeight="medium"
              mb="1"
            >
              Tipo *
            </Text>
            <OktonSelect
              size="lg"
              rounded="2xl"
              name="tipo"
              color={color}
              value={form.tipo}
              onChange={e => {
                const valor = e.target.value as 'nivel' | 'conta';
                setForm(f => ({
                  ...f,
                  tipo: valor,
                  // Trocar para Nível descarta a Conta: Nível não tem uma.
                  conta: valor === 'conta' ? f.conta : '',
                }));
              }}
            >
              <option value="nivel">Nível</option>
              <option value="conta">Conta</option>
            </OktonSelect>
            <Text fontSize="xs" color="gray.500" mt="1">
              {form.tipo === 'conta'
                ? 'A Conta é a folha da árvore: é ela que os lançamentos classificam.'
                : 'O Nível só agrupa — não recebe lançamento e não tem Conta.'}
            </Text>
          </Box>

          <SimpleGrid columns={[1, form.tipo === 'conta' ? 2 : 1]} spacing="4">
            <Box>
              <Text
                as="label"
                htmlFor="plano-contas-item"
                display="block"
                fontSize="sm"
                fontWeight="medium"
                mb="1"
              >
                Item *
              </Text>
              <OktonInput
                name="plano-contas-item"
                size="lg"
                rounded="2xl"
                fontWeight="light"
                color={color}
                maxLength={100}
                placeholder="Ex.: 1.1.2"
                value={form.item}
                onChange={e => {
                  const valor = e.target.value;
                  setForm(f => ({ ...f, item: valor }));
                }}
              />
              <Text
                fontSize="xs"
                color={itemRepetido ? 'red.500' : 'gray.500'}
                mt="1"
              >
                {form.item.trim() === ''
                  ? 'A numeração define onde o registro entra na árvore.'
                  : !itemValido
                  ? 'Só números separados por ponto, como 1.1.2.'
                  : ehRaiz
                  ? 'É uma raiz da árvore: não fica abaixo de nenhum Nível.'
                  : itemRepetido
                  ? `Este Item já existe.${
                      sugestao?.item
                        ? ` O próximo livre é ${sugestao.item}.`
                        : ''
                    }`
                  : nivelPai
                  ? `Entra abaixo de ${pai} — ${nivelPai.nome_pct}.`
                  : `O Nível ${pai} ainda não existe.`}
              </Text>
            </Box>

            {form.tipo === 'conta' && (
              <Box>
                <Text
                  as="label"
                  htmlFor="plano-contas-conta"
                  display="block"
                  fontSize="sm"
                  fontWeight="medium"
                  mb="1"
                >
                  Conta *
                </Text>
                <OktonInput
                  name="plano-contas-conta"
                  size="lg"
                  rounded="2xl"
                  fontWeight="light"
                  color={color}
                  type="number"
                  placeholder="Ex.: 9004"
                  value={form.conta}
                  onChange={e => {
                    const valor = e.target.value;
                    setForm(f => ({ ...f, conta: valor }));
                  }}
                />
                <Text fontSize="xs" color="gray.500" mt="1">
                  {sugestao?.conta &&
                  form.conta.trim() !== String(sugestao.conta)
                    ? `Próximo número livre neste Nível: ${sugestao.conta}.`
                    : 'Número contábil da Conta. Não se repete no Plano de Contas.'}
                </Text>
              </Box>
            )}
          </SimpleGrid>

          <Box>
            <Text
              as="label"
              htmlFor="plano-contas-nome"
              display="block"
              fontSize="sm"
              fontWeight="medium"
              mb="1"
            >
              Nome *
            </Text>
            <OktonInput
              name="plano-contas-nome"
              size="lg"
              rounded="2xl"
              fontWeight="light"
              color={color}
              maxLength={60}
              placeholder={
                form.tipo === 'conta'
                  ? 'Ex.: Recebimentos de Clientes'
                  : 'Ex.: ENTRADAS OPERACIONAIS'
              }
              value={form.nome}
              onChange={e => {
                const valor = e.target.value;
                setForm(f => ({ ...f, nome: valor }));
              }}
            />
            {form.tipo === 'nivel' && (
              <Text fontSize="xs" color="gray.500" mt="1">
                O nome do Nível é gravado em maiúsculas, como no IFLEX.
              </Text>
            )}
          </Box>

          <Flex gridGap="3" pt="2">
            <Button
              variant="ghost"
              onClick={() => history.push('/plano-contas')}
            >
              Cancelar
            </Button>
            <Button
              colorScheme="blue"
              leftIcon={
                saveMutation.isLoading ? (
                  <Spinner size="sm" />
                ) : (
                  <Check size={16} />
                )
              }
              isDisabled={saveMutation.isLoading}
              onClick={salvar}
            >
              Salvar
            </Button>
          </Flex>
        </Stack>
      </Box>
    </BankAppPage>
  );
};

export default PlanoContasForm;
